/**
 * Purchase Order Detail Page
 * 
 * Full PO management with line items, pricing, notes - mirrors Order detail page
 */
import React, { useState, useEffect, useCallback } from 'react';
import { purchaseOrdersAPI, vendorsAPI, vendorBillsAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useRefetchOnReturn } from '../../hooks';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import EntityActivityPanel from '../../components/admin/EntityActivityPanel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  ShoppingBag,
  Building2,
  Plus,
  Loader2,
  ArrowLeft,
  Edit2,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  FileText,
  Calendar,
  DollarSign,
  Package,
  Eye,
  Save,
  AlertTriangle,
  RefreshCw,
  Printer,
} from 'lucide-react';
import ChecksSection from '../../components/admin/ChecksSection';

// Line item types
const LINE_ITEM_TYPES = [
  { value: 'material', label: 'Material' },
  { value: 'product_blank', label: 'Product Blank' },
  { value: 'service', label: 'Service' },
  { value: 'freight', label: 'Freight' },
  { value: 'other', label: 'Other' },
];

// Status configuration
const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: Edit2 },
  sent: { label: 'Sent', color: 'bg-blue-100 text-blue-700', icon: Send },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-700', icon: Package },
  closed: { label: 'Closed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle },
};

// Bill status colors (NO partial - only unpaid/paid/void)
const BILL_STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  unpaid: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  void: 'bg-gray-100 text-gray-500',
};

// Format currency (from cents)
const formatCurrency = (cents) => {
  if (cents === null || cents === undefined) return '$0.00';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const PurchaseOrderDetailPage = ({ 
  poId, 
  onBack, 
  onNavigateToVendor,
  onNavigateToBill,
  onCreateBill 
}) => {
  const { isAdmin } = useAuth();
  
  // Data state
  const [po, setPo] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [lines, setLines] = useState([]);
  const [bills, setBills] = useState([]);
  const [lockStatus, setLockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Editing state
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  
  // Line item modal state
  const [showLineModal, setShowLineModal] = useState(false);
  const [editingLine, setEditingLine] = useState(null);
  const [lineData, setLineData] = useState({
    item_type: 'material',
    sku: '',
    description: '',
    qty: 1,
    uom: 'each',
    unit_cost: '', // Store as string for smooth editing, convert to cents on save
  });
  const [savingLine, setSavingLine] = useState(false);
  
  // Action states
  const [actionLoading, setActionLoading] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Sync modal state (Phase 4)
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncingBill, setSyncingBill] = useState(false);
  
  // Print preview state
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  
  // Computed
  const isDraft = po?.status === 'draft';
  // PO is editable only if it's draft AND not locked by a finalized/paid bill
  const isLockedByBill = lockStatus?.is_locked === true;
  const canEdit = isDraft && !isLockedByBill;
  
  // Check if there's a draft bill that might need syncing
  const draftBill = bills?.find(b => b.status === 'draft');
  const hasDraftBillMismatch = draftBill && draftBill.amount_cents !== po?.total_cents;
  
  // Fetch PO data
  const fetchPO = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await purchaseOrdersAPI.getPurchaseOrder(poId);
      setPo(data.purchase_order);
      setVendor(data.vendor);
      setLines(data.lines || []);
      setBills(data.bills || []);
      setLockStatus(data.lock_status || null);
      setNotesValue(data.purchase_order?.notes || '');
      
    } catch (err) {
      setError(err.message || 'Failed to load purchase order');
      toast.error(err.message || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [poId]);
  
  useEffect(() => {
    if (poId) {
      fetchPO();
    }
  }, [poId, fetchPO]);

  // Handle manual refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchPO();
    } finally {
      setRefreshing(false);
    }
  };

  // Auto-refetch on tab focus or route return
  useRefetchOnReturn({
    refetch: fetchPO,
    enabled: !loading && !!poId,
    refreshing,
    throttleMs: 1500,
  });
  
  // Save notes
  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await purchaseOrdersAPI.updatePurchaseOrder(poId, { notes: notesValue });
      setPo(prev => ({ ...prev, notes: notesValue }));
      setEditingNotes(false);
      toast.success('Notes saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };
  
  // Open line modal for add/edit
  const openLineModal = (line = null) => {
    if (line) {
      setEditingLine(line);
      setLineData({
        item_type: line.item_type || 'material',
        sku: line.sku || '',
        description: line.description || '',
        qty: line.qty || 1,
        uom: line.uom || 'each',
        unit_cost: line.unit_cost_cents ? (line.unit_cost_cents / 100).toString() : '',
      });
    } else {
      setEditingLine(null);
      setLineData({
        item_type: 'material',
        sku: '',
        description: '',
        qty: 1,
        uom: 'each',
        unit_cost: '',
      });
    }
    setShowLineModal(true);
  };
  
  // Save line item
  const handleSaveLine = async () => {
    if (!lineData.description?.trim()) {
      toast.error('Description is required');
      return;
    }
    if (lineData.qty <= 0) {
      toast.error('Quantity must be greater than 0');
      return;
    }
    
    // Convert unit_cost string to cents
    const unitCostCents = Math.round(parseFloat(lineData.unit_cost || 0) * 100);
    
    setSavingLine(true);
    try {
      const payload = {
        item_type: lineData.item_type,
        sku: lineData.sku || null,
        description: lineData.description,
        qty: lineData.qty,
        uom: lineData.uom || 'each',
        unit_cost_cents: unitCostCents,
      };
      
      if (editingLine) {
        await purchaseOrdersAPI.updateLine(poId, editingLine.id, payload);
        toast.success('Line item updated');
      } else {
        await purchaseOrdersAPI.addLine(poId, payload);
        toast.success('Line item added');
      }
      
      setShowLineModal(false);
      await fetchPO();
      
      // Check if there's a draft bill that needs syncing (Phase 4)
      if (draftBill) {
        setShowSyncModal(true);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save line item');
    } finally {
      setSavingLine(false);
    }
  };
  
  // Delete line item
  const handleDeleteLine = async (lineId) => {
    if (!window.confirm('Delete this line item?')) return;
    
    try {
      await purchaseOrdersAPI.deleteLine(poId, lineId);
      toast.success('Line item deleted');
      await fetchPO();
      
      // Check if there's a draft bill that needs syncing (Phase 4)
      if (draftBill) {
        setShowSyncModal(true);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to delete line item');
    }
  };
  
  // Sync draft bill from PO (Phase 4)
  const handleSyncBillFromPO = async () => {
    if (!draftBill) return;
    
    setSyncingBill(true);
    try {
      await vendorBillsAPI.syncFromPO(draftBill.id);
      toast.success('Draft Bill synced with PO');
      setShowSyncModal(false);
      fetchPO();
    } catch (err) {
      toast.error(err.message || 'Failed to sync bill');
    } finally {
      setSyncingBill(false);
    }
  };
  
  // PO Actions
  const handleSendPO = async () => {
    if (lines.length === 0) {
      toast.error('Add at least one line item before sending');
      return;
    }
    
    setActionLoading('send');
    try {
      const result = await purchaseOrdersAPI.sendPurchaseOrder(poId);
      toast.success('Purchase order sent');
      
      // Check if auto-bill was created
      if (result.auto_bill) {
        toast.success(
          <div className="cursor-pointer" onClick={() => onNavigateToBill?.(result.auto_bill.bill_id)}>
            Draft Bill created from PO - Click to view
          </div>,
          { duration: 5000 }
        );
      }
      
      fetchPO();
    } catch (err) {
      toast.error(err.message || 'Failed to send PO');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleCancelPO = async () => {
    if (!window.confirm('Cancel this purchase order?')) return;
    
    setActionLoading('cancel');
    try {
      await purchaseOrdersAPI.cancelPurchaseOrder(poId);
      toast.success('Purchase order cancelled');
      fetchPO();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel PO');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleClosePO = async () => {
    if (!window.confirm('Close this purchase order? This cannot be undone.')) return;
    
    setActionLoading('close');
    try {
      await purchaseOrdersAPI.closePurchaseOrder(poId);
      toast.success('Purchase order closed');
      fetchPO();
    } catch (err) {
      toast.error(err.message || 'Failed to close PO');
    } finally {
      setActionLoading(null);
    }
  };
  
  // Calculate line total for display
  const calculateLineTotal = (qty, unitCostCents) => {
    return qty * unitCostCents;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }
  
  if (error || !po) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Purchase Orders
        </Button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || 'Purchase order not found'}
        </div>
      </div>
    );
  }
  
  const statusConfig = STATUS_CONFIG[po.status] || STATUS_CONFIG.draft;
  const StatusIcon = statusConfig.icon;
  
  return (
    <div className="space-y-6" data-testid="po-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={onBack} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 font-mono">{po.po_number}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={statusConfig.color}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {statusConfig.label}
                  </Badge>
                  <span className="text-gray-500">•</span>
                  <span className="text-gray-600">{formatDate(po.po_date)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Actions based on status */}
        <div className="flex items-center gap-2">
          {/* Preview/Print Button */}
          <Button 
            variant="outline"
            onClick={() => setShowPrintPreview(true)}
            data-testid="preview-print-btn"
            title="Preview & Print"
          >
            <Printer className="w-4 h-4 mr-2" />
            Preview
          </Button>
          
          {/* Refresh Button */}
          <Button 
            variant="ghost" 
            size="icon"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-po-btn"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
          
          {isDraft && (
            <>
              <Button 
                variant="outline" 
                onClick={handleCancelPO}
                disabled={actionLoading === 'cancel'}
                data-testid="cancel-po-btn"
              >
                {actionLoading === 'cancel' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4 mr-2" />}
                Cancel
              </Button>
              <Button 
                onClick={handleSendPO}
                disabled={actionLoading === 'send' || lines.length === 0}
                data-testid="send-po-btn"
              >
                {actionLoading === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send PO
              </Button>
            </>
          )}
          {po.status === 'sent' && (
            <Button 
              onClick={handleClosePO}
              disabled={actionLoading === 'close'}
              data-testid="close-po-btn"
            >
              {actionLoading === 'close' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Close PO
            </Button>
          )}
        </div>
      </div>
      
      {/* Locked by Bill Warning */}
      {isLockedByBill && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2" data-testid="po-locked-by-bill-warning">
          <AlertTriangle className="w-5 h-5" />
          <span>
            <strong>PO Locked:</strong> {lockStatus.reason}
            {lockStatus.bill_id && (
              <Button 
                variant="link" 
                className="text-red-800 underline p-0 h-auto ml-1"
                onClick={() => onNavigateToBill?.(lockStatus.bill_id)}
              >
                View Bill
              </Button>
            )}
          </span>
        </div>
      )}
      
      {/* Status Warning (non-draft, non-locked) */}
      {!canEdit && !isLockedByBill && po.status !== 'cancelled' && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          <span>This PO is <strong>{po.status}</strong> and cannot be edited.</span>
        </div>
      )}
      
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Vendor Card */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-400" />
                Vendor
              </h2>
              {vendor && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => onNavigateToVendor?.(vendor.id)}
                  data-testid="view-vendor-btn"
                >
                  View Vendor
                </Button>
              )}
            </div>
            {vendor ? (
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-gray-500" />
                </div>
                <div>
                  <p className="font-semibold text-lg">{vendor.name}</p>
                  <p className="text-gray-500 text-sm">{vendor.email || '—'}</p>
                </div>
              </div>
            ) : (
              <p className="text-gray-400">No vendor assigned</p>
            )}
          </div>
          
          {/* Line Items */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Line Items
                {lines.length > 0 && <Badge variant="outline">{lines.length}</Badge>}
              </h2>
              {canEdit && (
                <Button size="sm" onClick={() => openLineModal()} data-testid="add-line-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Line
                </Button>
              )}
            </div>
            
            {lines.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium w-8">#</th>
                      <th className="px-3 py-2 text-left font-medium">Description</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-right font-medium">Qty</th>
                      <th className="px-3 py-2 text-right font-medium">Unit Cost</th>
                      <th className="px-3 py-2 text-right font-medium">Total</th>
                      {canEdit && <th className="px-3 py-2 w-20"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lines.map((line, idx) => (
                      <tr key={line.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400">{idx + 1}</td>
                        <td className="px-3 py-2">
                          <div>
                            <p className="font-medium whitespace-pre-wrap">{line.description}</p>
                            {line.sku && <p className="text-xs text-gray-500 font-mono">{line.sku}</p>}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant="outline" className="capitalize text-xs">
                            {line.item_type?.replace('_', ' ')}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {line.qty} {line.uom}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {formatCurrency(line.unit_cost_cents)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">
                          {formatCurrency(line.line_total_cents)}
                        </td>
                        {canEdit && (
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openLineModal(line)}
                                data-testid={`edit-line-${line.id}`}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteLine(line.id)}
                                className="text-red-600 hover:text-red-700"
                                data-testid={`delete-line-${line.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-medium">
                    <tr>
                      <td colSpan={canEdit ? 5 : 5} className="px-3 py-2 text-right">Subtotal</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(po.subtotal_cents)}</td>
                      {canEdit && <td></td>}
                    </tr>
                    {(po.shipping_cents || 0) > 0 && (
                      <tr>
                        <td colSpan={canEdit ? 5 : 5} className="px-3 py-2 text-right">Shipping</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(po.shipping_cents)}</td>
                        {canEdit && <td></td>}
                      </tr>
                    )}
                    {(po.tax_cents || 0) > 0 && (
                      <tr>
                        <td colSpan={canEdit ? 5 : 5} className="px-3 py-2 text-right">Tax</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(po.tax_cents)}</td>
                        {canEdit && <td></td>}
                      </tr>
                    )}
                    <tr className="text-lg">
                      <td colSpan={canEdit ? 5 : 5} className="px-3 py-3 text-right font-bold">Total</td>
                      <td className="px-3 py-3 text-right tabular-nums font-bold">{formatCurrency(po.total_cents)}</td>
                      {canEdit && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No line items yet</p>
                {canEdit && (
                  <Button size="sm" className="mt-2" onClick={() => openLineModal()}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add First Line Item
                  </Button>
                )}
              </div>
            )}
          </div>
          
          {/* Notes */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Notes</h2>
              {canEdit && !editingNotes && (
                <Button variant="ghost" size="sm" onClick={() => setEditingNotes(true)}>
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {editingNotes ? (
              <div className="space-y-3">
                <Textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  rows={4}
                  placeholder="Add notes for this purchase order..."
                  data-testid="po-notes-input"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSaveNotes} disabled={savingNotes}>
                    {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                    Save
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setEditingNotes(false);
                      setNotesValue(po.notes || '');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 whitespace-pre-wrap">
                {po.notes || <span className="text-gray-400">No notes</span>}
              </p>
            )}
          </div>
          
          {/* Related Bills */}
          <div className="bg-white rounded-xl border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-gray-400" />
                Related Bills
                {bills.length > 0 && <Badge variant="outline">{bills.length}</Badge>}
              </h2>
              <Button size="sm" onClick={() => onCreateBill?.(po, vendor)} data-testid="create-bill-btn">
                <Plus className="w-4 h-4 mr-1" />
                Create Bill
              </Button>
            </div>
            
            {bills.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Bill #</th>
                      <th className="px-3 py-2 text-left font-medium">Status</th>
                      <th className="px-3 py-2 text-right font-medium">Amount</th>
                      <th className="px-3 py-2 text-right font-medium">Balance</th>
                      <th className="px-3 py-2 text-left font-medium">Due</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bills.map(bill => {
                      const hasMismatch = bill.status === 'draft' && bill.amount_cents !== po?.total_cents;
                      return (
                        <tr 
                          key={bill.id} 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => onNavigateToBill?.(bill.id)}
                        >
                          <td className="px-3 py-2 font-mono">
                            {bill.bill_number || bill.id.slice(0, 8)}
                            {hasMismatch && (
                              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300 text-xs">
                                Differs from PO
                              </Badge>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Badge className={BILL_STATUS_COLORS[bill.status]}>
                              {bill.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(bill.amount_cents)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <span className={bill.balance_cents > 0 ? 'text-orange-600' : 'text-green-600'}>
                              {formatCurrency(bill.balance_cents)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-500">{formatDate(bill.due_date)}</td>
                          <td className="px-3 py-2">
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No bills linked to this PO</p>
              </div>
            )}
          </div>

          {/* Related Checks Section */}
          <ChecksSection
            poId={poId}
            title="Related Checks"
            limit={5}
            emptyMessage="No checks related to this PO yet"
          />
        </div>
        
        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Totals Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="tabular-nums">{formatCurrency(po.subtotal_cents)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Shipping</span>
                <span className="tabular-nums">{formatCurrency(po.shipping_cents || 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax</span>
                <span className="tabular-nums">{formatCurrency(po.tax_cents || 0)}</span>
              </div>
              <div className="flex justify-between pt-3 border-t text-lg font-bold">
                <span>Total</span>
                <span className="tabular-nums">{formatCurrency(po.total_cents)}</span>
              </div>
            </div>
          </div>
          
          {/* Details Card */}
          <div className="bg-white rounded-xl border p-6">
            <h2 className="text-lg font-semibold mb-4">Details</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">PO Number</span>
                <span className="font-mono">{po.po_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Status</span>
                <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">PO Date</span>
                <span>{formatDate(po.po_date)}</span>
              </div>
              {po.needed_by_date && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Needed By</span>
                  <span>{formatDate(po.needed_by_date)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Line Items</span>
                <span>{lines.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Created</span>
                <span>{formatDate(po.created_at)}</span>
              </div>
            </div>
          </div>
          
          {/* Activity Panel */}
          <EntityActivityPanel 
            entityType="PURCHASE_ORDER" 
            entityId={po.id} 
            title="Activity"
          />
        </div>
      </div>
      
      {/* Line Item Modal */}
      <Dialog open={showLineModal} onOpenChange={setShowLineModal}>
        <DialogContent className="max-w-lg" data-testid="line-item-modal">
          <DialogHeader>
            <DialogTitle>{editingLine ? 'Edit Line Item' : 'Add Line Item'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Item Type</Label>
                <Select
                  value={lineData.item_type}
                  onValueChange={(v) => setLineData({ ...lineData, item_type: v })}
                >
                  <SelectTrigger data-testid="line-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LINE_ITEM_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>SKU (optional)</Label>
                <Input
                  value={lineData.sku}
                  onChange={(e) => setLineData({ ...lineData, sku: e.target.value })}
                  placeholder="SKU-001"
                  data-testid="line-sku-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Description *</Label>
              <Textarea
                value={lineData.description}
                onChange={(e) => setLineData({ ...lineData, description: e.target.value })}
                placeholder="Item description (supports multiple lines)"
                rows={3}
                data-testid="line-description-input"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Quantity *</Label>
                <Input
                  type="number"
                  min="1"
                  value={lineData.qty}
                  onChange={(e) => setLineData({ ...lineData, qty: parseInt(e.target.value) || 1 })}
                  data-testid="line-qty-input"
                />
              </div>
              <div>
                <Label>UOM</Label>
                <Input
                  value={lineData.uom}
                  onChange={(e) => setLineData({ ...lineData, uom: e.target.value })}
                  placeholder="each"
                  data-testid="line-uom-input"
                />
              </div>
              <div>
                <Label>Unit Cost ($)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={lineData.unit_cost}
                  onChange={(e) => setLineData({ ...lineData, unit_cost: e.target.value })}
                  placeholder="0.00"
                  data-testid="line-cost-input"
                />
              </div>
            </div>
            
            {/* Line Total Preview */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between font-medium">
                <span>Line Total</span>
                <span className="tabular-nums">
                  {formatCurrency(lineData.qty * Math.round(parseFloat(lineData.unit_cost || 0) * 100))}
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLineModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveLine} disabled={savingLine} data-testid="save-line-btn">
              {savingLine ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingLine ? 'Update' : 'Add'} Line
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Sync Bill Modal (Phase 4) */}
      <Dialog open={showSyncModal} onOpenChange={setShowSyncModal}>
        <DialogContent className="max-w-md" data-testid="sync-bill-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Update Draft Bill?
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-gray-600">
              PO totals have changed. Would you like to update the linked Draft Bill to match?
            </p>
            
            {draftBill && (
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Current Bill Amount:</span>
                  <span className="font-medium">{formatCurrency(draftBill.amount_cents)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">New PO Total:</span>
                  <span className="font-medium text-blue-600">{formatCurrency(po?.total_cents || 0)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between">
                  <span className="text-gray-500">Difference:</span>
                  <span className={`font-medium ${(po?.total_cents || 0) - draftBill.amount_cents > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {(po?.total_cents || 0) - draftBill.amount_cents > 0 ? '+' : ''}{formatCurrency((po?.total_cents || 0) - draftBill.amount_cents)}
                  </span>
                </div>
              </div>
            )}
            
            <p className="text-xs text-gray-500">
              If you choose &quot;No&quot;, the bill will remain unchanged. A mismatch badge will appear on both records.
            </p>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowSyncModal(false);
                toast.info('Bill not synced. Bill differs from PO.');
              }}
            >
              No, Keep Bill As-Is
            </Button>
            <Button 
              onClick={handleSyncBillFromPO} 
              disabled={syncingBill}
              data-testid="sync-bill-btn"
            >
              {syncingBill ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, Sync Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Print Preview Modal */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto p-0">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
            <DialogTitle className="text-lg font-semibold">Purchase Order Preview</DialogTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPrintPreview(false)}
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  const printContent = document.getElementById('po-print-content');
                  if (printContent) {
                    const printWindow = window.open('', '_blank');
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>PO ${po.po_number}</title>
                          <style>
                            * { margin: 0; padding: 0; box-sizing: border-box; }
                            body { 
                              font-family: Helvetica, Arial, sans-serif; 
                              font-size: 10pt; 
                              color: #1f2937;
                              line-height: 1.4;
                            }
                            @media print {
                              body { 
                                print-color-adjust: exact; 
                                -webkit-print-color-adjust: exact; 
                              }
                            }
                          </style>
                        </head>
                        <body>
                          ${printContent.innerHTML}
                        </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                      printWindow.print();
                    }, 250);
                  }
                }}
                data-testid="print-po-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </div>
          </div>
          
          {/* Printable Content */}
          <div id="po-print-content" className="bg-white">
            <div style={{ 
              maxWidth: '8in', 
              margin: '0 auto', 
              padding: '32px 40px',
              fontFamily: 'Helvetica, Arial, sans-serif',
              fontSize: '10pt',
              color: '#1f2937'
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
                {/* Left: Company Info */}
                <div>
                  <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#111', marginBottom: '4px' }}>
                    Corporate Awards & Engraving
                  </div>
                  <div style={{ fontSize: '9pt', color: '#4b5563', lineHeight: '1.5' }}>
                    1489 E. Warner Ave, Santa Ana, CA 92705<br />
                    (949) 261-7130 | ca@engraveca.com
                  </div>
                </div>
                
                {/* Right: PO Info - Clean Stack */}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '13pt', fontWeight: 'bold', color: '#1E3A5F', marginBottom: '6px' }}>
                    PURCHASE ORDER
                  </div>
                  <div style={{ fontSize: '15pt', fontWeight: 'bold', color: '#111', marginBottom: '2px' }}>
                    {po.po_number}
                  </div>
                  <div style={{ fontSize: '10pt', color: '#6b7280' }}>
                    {formatDate(po.po_date)}
                  </div>
                </div>
              </div>
              
              {/* Navy Divider */}
              <div style={{ borderTop: '2px solid #1E3A5F', marginBottom: '24px' }} />
              
              {/* Address Boxes - Three Columns */}
              <div style={{ 
                display: 'flex', 
                gap: '16px', 
                marginBottom: '24px'
              }}>
                {/* Vendor Box */}
                <div style={{ 
                  flex: 1, 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    backgroundColor: '#f3f4f6', 
                    padding: '8px 12px', 
                    borderBottom: '1px solid #d1d5db',
                    fontSize: '8pt', 
                    fontWeight: 'bold', 
                    color: '#1E3A5F', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Vendor
                  </div>
                  <div style={{ padding: '12px', fontSize: '9pt', lineHeight: '1.5' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{vendor?.name || 'N/A'}</div>
                    {/* Primary Contact */}
                    {(() => {
                      const primaryContact = (vendor?.contacts || []).find(c => c.is_primary);
                      if (primaryContact) {
                        return (
                          <div style={{ marginBottom: '4px', color: '#4b5563' }}>
                            <div style={{ fontStyle: 'italic' }}>Attn: {primaryContact.name}</div>
                            {primaryContact.email && <div>{primaryContact.email}</div>}
                            {primaryContact.phone && <div>{primaryContact.phone}</div>}
                          </div>
                        );
                      }
                      return null;
                    })()}
                    {vendor?.address && (
                      <div style={{ color: '#4b5563' }}>
                        {vendor.address.line1 && <div>{vendor.address.line1}</div>}
                        {vendor.address.line2 && <div>{vendor.address.line2}</div>}
                        {(vendor.address.city || vendor.address.state || vendor.address.zip_code) && (
                          <div>{[vendor.address.city, vendor.address.state, vendor.address.zip_code].filter(Boolean).join(', ')}</div>
                        )}
                      </div>
                    )}
                    {!(vendor?.contacts || []).find(c => c.is_primary) && (
                      <div style={{ color: '#4b5563' }}>
                        {vendor?.email && <div>{vendor.email}</div>}
                        {vendor?.phone && <div>{vendor.phone}</div>}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Ship To Box */}
                <div style={{ 
                  flex: 1, 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    backgroundColor: '#f3f4f6', 
                    padding: '8px 12px', 
                    borderBottom: '1px solid #d1d5db',
                    fontSize: '8pt', 
                    fontWeight: 'bold', 
                    color: '#1E3A5F', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Ship To
                  </div>
                  <div style={{ padding: '12px', fontSize: '9pt', lineHeight: '1.5' }}>
                    {po.ship_to_address ? (
                      <>
                        {po.ship_to_address.name && <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{po.ship_to_address.name}</div>}
                        <div style={{ color: '#4b5563' }}>
                          {po.ship_to_address.line1 && <div>{po.ship_to_address.line1}</div>}
                          {po.ship_to_address.line2 && <div>{po.ship_to_address.line2}</div>}
                          {(po.ship_to_address.city || po.ship_to_address.state || po.ship_to_address.zip_code) && (
                            <div>{[po.ship_to_address.city, po.ship_to_address.state, po.ship_to_address.zip_code].filter(Boolean).join(', ')}</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Corporate Awards & Engraving</div>
                        <div style={{ color: '#4b5563' }}>
                          <div>1489 E. Warner Ave</div>
                          <div>Santa Ana, CA 92705</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Bill To Box */}
                <div style={{ 
                  flex: 1, 
                  border: '1px solid #d1d5db', 
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}>
                  <div style={{ 
                    backgroundColor: '#f3f4f6', 
                    padding: '8px 12px', 
                    borderBottom: '1px solid #d1d5db',
                    fontSize: '8pt', 
                    fontWeight: 'bold', 
                    color: '#1E3A5F', 
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Bill To
                  </div>
                  <div style={{ padding: '12px', fontSize: '9pt', lineHeight: '1.5' }}>
                    {po.bill_to_address ? (
                      <>
                        {po.bill_to_address.name && <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{po.bill_to_address.name}</div>}
                        <div style={{ color: '#4b5563' }}>
                          {po.bill_to_address.line1 && <div>{po.bill_to_address.line1}</div>}
                          {po.bill_to_address.line2 && <div>{po.bill_to_address.line2}</div>}
                          {(po.bill_to_address.city || po.bill_to_address.state || po.bill_to_address.zip_code) && (
                            <div>{[po.bill_to_address.city, po.bill_to_address.state, po.bill_to_address.zip_code].filter(Boolean).join(', ')}</div>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Corporate Awards & Engraving</div>
                        <div style={{ color: '#4b5563' }}>
                          <div>1489 E. Warner Ave</div>
                          <div>Santa Ana, CA 92705</div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Line Items Table */}
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse', 
                marginBottom: '24px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                overflow: 'hidden'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#1E3A5F' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '9pt', fontWeight: '600', color: 'white' }}>Description</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '9pt', fontWeight: '600', color: 'white' }}>SKU</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: '9pt', fontWeight: '600', color: 'white', width: '60px' }}>Qty</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '9pt', fontWeight: '600', color: 'white', width: '100px' }}>Unit Cost</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '9pt', fontWeight: '600', color: 'white', width: '100px' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={line.id || index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '10px 14px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{line.description || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{line.sku || '—'}</td>
                      <td style={{ padding: '10px 14px', fontSize: '9pt', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>{line.qty}</td>
                      <td style={{ padding: '10px 14px', fontSize: '9pt', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{formatCurrency(line.unit_cost_cents)}</td>
                      <td style={{ padding: '10px 14px', fontSize: '9pt', textAlign: 'right', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{formatCurrency(line.line_total_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Totals - Right Aligned */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
                <div style={{ width: '240px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '10pt', color: '#4b5563' }}>
                    <span>Subtotal:</span>
                    <span style={{ fontFamily: 'monospace' }}>{formatCurrency(po.subtotal_cents)}</span>
                  </div>
                  {(po.shipping_cents > 0) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '10pt', color: '#4b5563' }}>
                      <span>Shipping:</span>
                      <span style={{ fontFamily: 'monospace' }}>{formatCurrency(po.shipping_cents)}</span>
                    </div>
                  )}
                  {(po.tax_cents > 0) && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '10pt', color: '#4b5563' }}>
                      <span>Tax:</span>
                      <span style={{ fontFamily: 'monospace' }}>{formatCurrency(po.tax_cents)}</span>
                    </div>
                  )}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    padding: '10px 0', 
                    fontSize: '12pt', 
                    fontWeight: 'bold',
                    borderTop: '2px solid #1E3A5F', 
                    marginTop: '8px',
                    color: '#111'
                  }}>
                    <span>Total:</span>
                    <span style={{ fontFamily: 'monospace' }}>{formatCurrency(po.total_cents)}</span>
                  </div>
                </div>
              </div>
              
              {/* Notes */}
              {po.notes && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '12px 16px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1E3A5F', marginBottom: '6px', textTransform: 'uppercase' }}>
                    Notes
                  </div>
                  <div style={{ fontSize: '9pt', color: '#4b5563', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                    {po.notes}
                  </div>
                </div>
              )}
              
              {/* Terms & Conditions */}
              <div style={{ 
                padding: '12px 16px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1E3A5F', marginBottom: '6px', textTransform: 'uppercase' }}>
                  Terms & Conditions
                </div>
                <div style={{ fontSize: '8pt', color: '#6b7280', lineHeight: 1.5 }}>
                  Please reference PO number on all shipments and correspondence. Net 30 unless otherwise specified.
                </div>
              </div>
              
              {/* Footer */}
              <div style={{ 
                marginTop: '24px', 
                paddingTop: '16px',
                borderTop: '1px solid #e5e7eb',
                textAlign: 'center', 
                fontSize: '8pt', 
                color: '#9ca3af' 
              }}>
                {po.po_number} | Page 1 of 1
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseOrderDetailPage;
